
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import {
  ColumnDefinition,
  TableAction,
} from '@shared/components/master-table/master-table.component';
import {
  DailyScheduleConfig,
  DailyScheduleCreatePayload,
  DailyScheduleFormValue,
  DailyScheduleMealBreak,
  DailyScheduleMode,
  DailyScheduleUpdatePayload,
  MealBreakMode,
} from './daily-schedule.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { JourneyConfig } from '../journey/journey.interface';

type DailyScheduleWorkspaceMode = 'view' | 'create' | 'edit';

interface CompaniesRecordsResponse {
  items?: CompaniesRecord[];
  data?: CompaniesRecord[];
  results?: CompaniesRecord[];
  companiesRecords?: CompaniesRecord[];
}

@Component({
  selector: 'app-daily-schedule-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './daily-schedule-detail.component.html',
  styleUrl: './daily-schedule-detail.component.scss',
})
export class DailyScheduleDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: DailyScheduleWorkspaceMode = 'view';
  schedules: DailyScheduleConfig[] = [];
  companies: CompaniesRecord[] = [];
  journeys: JourneyConfig[] = [];
  selectedCompanyId = '';
  selectedScheduleId: string | null = null;
  selectedSchedule: DailyScheduleConfig | null = null;
  dataSource = new MatTableDataSource<DailyScheduleConfig>([]);
  actions: TableAction<DailyScheduleConfig>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  form: DailyScheduleFormValue = this.createEmptyForm();

  readonly readAccess = { resource: 'dailySchedules', action: 'read' } as const;
  readonly createAccess = { resource: 'dailySchedules', action: 'create' } as const;
  readonly updateAccess = { resource: 'dailySchedules', action: 'update' } as const;
  readonly deleteAccess = { resource: 'dailySchedules', action: 'delete' } as const;

  readonly scheduleModes: DailyScheduleMode[] = [
    'fixed_full',
    'fixed_start_flexible_end',
    'free_start_duration',
    'entry_window_duration',
    'hours_only',
    'split_shift',
    'open_shift_with_limits',
  ];

  readonly mealBreakModes: MealBreakMode[] = ['fixed', 'flexible', 'free'];

  ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.schedules = this.schedules.map((schedule) => this.normalizeSchedule(schedule));
        this.dataSource.data = this.schedules;
        if (this.selectedSchedule) {
          this.selectedSchedule = this.normalizeSchedule(this.selectedSchedule);
        }
        this.configureTable();
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    this.configureTable();
    this.dataSource.filterPredicate = (schedule, filter) =>
      [
        schedule.name,
        schedule.scheduleSummary,
        schedule.mealBreakSummary,
        schedule.modeLabel,
        schedule.activeLabel,
      ]
        .join(' ')
        .toLowerCase()
        .includes(filter);

    void this.loadCompanies(false).then(() => this.syncWithRoute());
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('dailySchedulesCatalog.detail.createTitle');
    }

    if (this.isEditMode) {
      return this.t('dailySchedulesCatalog.detail.editTitle');
    }

    return this.selectedSchedule?.name || this.t('dailySchedulesCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('dailySchedulesCatalog.detail.createDescription');
    }

    if (this.isEditMode) {
      return this.t('dailySchedulesCatalog.detail.editDescription');
    }

    return this.t('dailySchedulesCatalog.detail.description');
  }

  get activeSchedulesCount(): number {
    return this.schedules.filter((schedule) => schedule.isActive).length;
  }

  get scheduleWithMealBreakCount(): number {
    return this.schedules.filter((schedule) => schedule.mealBreak.enabled).length;
  }

  get overnightSchedulesCount(): number {
    return this.schedules.filter((schedule) => schedule.crossesMidnight).length;
  }

  get splitShiftCount(): number {
    return this.schedules.filter((schedule) => schedule.mode === 'split_shift').length;
  }

  get hasMultipleCompanies(): boolean {
    return this.companies.length > 1;
  }

  get usesFixedTimeRange(): boolean {
    return this.form.mode === 'fixed_full' || this.form.mode === 'fixed_start_flexible_end';
  }

  get usesEntryWindow(): boolean {
    return this.form.mode === 'entry_window_duration';
  }

  get usesWorkMinuteLimits(): boolean {
    return this.form.mode === 'open_shift_with_limits';
  }

  get isSplitShiftMode(): boolean {
    return this.form.mode === 'split_shift';
  }

  get supportsCrossesMidnight(): boolean {
    return this.form.mode === 'fixed_full' || this.form.mode === 'fixed_start_flexible_end';
  }

  get mealBreakEnabled(): boolean {
    return this.form.mealBreak.enabled;
  }

  get fixedMealBreakMode(): boolean {
    return this.form.mealBreak.mode === 'fixed';
  }

  get flexibleMealBreakMode(): boolean {
    return this.form.mealBreak.mode === 'flexible';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get formCompanyName(): string {
    return this.companies.find(
      (company) => company.companiesRecordId === this.form.companiesRecordId
    )?.name || '-';
  }

  get formStartTimeLabel(): string {
    return this.formatClockTime(this.form.startTime);
  }

  get formEndTimeLabel(): string {
    return this.formatClockTime(this.form.endTime);
  }

  get associatedJourney(): JourneyConfig | null {
    const journeyId = this.selectedSchedule?.journeyId;
    return journeyId ? this.journeys.find((journey) => journey.journeyId === journeyId) || null : null;
  }

  get scheduleTotalMinutes(): number {
    const schedule = this.selectedSchedule;
    if (!schedule) return 0;
    if (schedule.startTime && schedule.endTime) {
      return this.durationBetweenTimes(schedule.startTime, schedule.endTime);
    }
    if (schedule.blocks?.length) {
      return schedule.blocks.reduce((total, block) => total + this.durationBetweenTimes(block.startTime, block.endTime), 0);
    }
    return Number(schedule.maximumWorkMinutes || schedule.minimumWorkMinutes || this.associatedJourney?.workMinutes || 0);
  }

  get scheduleMealMinutes(): number {
    return this.selectedSchedule?.mealBreak.enabled
      ? Math.max(0, Number(this.selectedSchedule.mealBreak.minutes || 0))
      : 0;
  }

  get scheduleEffectiveMinutes(): number {
    return Number(this.associatedJourney?.workMinutes || Math.max(0, this.scheduleTotalMinutes - this.scheduleMealMinutes));
  }

  get scheduleStartLabel(): string {
    return this.formatClockTime(this.selectedSchedule?.startTime);
  }

  get scheduleEndLabel(): string {
    return this.formatClockTime(this.selectedSchedule?.endTime);
  }

  get mealTimelineStartPercent(): number {
    if (!this.selectedSchedule?.mealBreak.enabled || !this.scheduleTotalMinutes) return 100;
    const mealStart = this.selectedSchedule.mealBreak.startTime;
    if (this.selectedSchedule.startTime && mealStart) {
      return Math.min(100, Math.max(0, this.durationBetweenTimes(this.selectedSchedule.startTime, mealStart) / this.scheduleTotalMinutes * 100));
    }
    return Math.max(0, (100 - this.mealTimelineWidthPercent) / 2);
  }

  get mealTimelineWidthPercent(): number {
    if (!this.scheduleTotalMinutes) return 0;
    return Math.min(100, this.scheduleMealMinutes / this.scheduleTotalMinutes * 100);
  }

  get mealTimelineEndPercent(): number {
    return Math.max(0, 100 - this.mealTimelineStartPercent - this.mealTimelineWidthPercent);
  }

  get associatedJourneyTypeLabel(): string {
    const type = this.associatedJourney?.journeyType;
    return type ? this.t(`journeysCatalog.journeyTypeOptions.${type}`) : '-';
  }

  get associatedJourneyTypeIcon(): string {
    switch (`${this.associatedJourney?.journeyType || ''}`.toLowerCase()) {
      case 'nighttime': return 'dark_mode';
      case 'mixed': return 'brightness_6';
      default: return 'light_mode';
    }
  }

  formatDuration(minutes: number): string {
    const safeMinutes = Math.max(0, Math.round(Number(minutes) || 0));
    const hours = Math.floor(safeMinutes / 60);
    const remainder = safeMinutes % 60;
    if (!remainder) return `${hours} h`;
    return `${hours} h ${remainder} min`;
  }

  viewAssociatedJourney(): void {
    if (this.selectedSchedule?.journeyId) {
      void this.router.navigate(['/attendance/journeys', this.selectedSchedule.journeyId]);
    }
  }

  async loadSchedules(
    preferredId: string | null = this.selectedScheduleId,
    navigateSelection = true
  ): Promise<void> {
    if (!this.selectedCompanyId) {
      this.schedules = [];
      this.journeys = [];
      this.dataSource.data = [];
      this.selectedScheduleId = null;
      this.selectedSchedule = null;
      this.mode = 'view';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      await this.loadJourneys();

      const response = await firstValueFrom(
        this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.schedules = (response || []).map((schedule) => this.normalizeSchedule(schedule));
      this.dataSource.data = this.schedules;

      const requestedSchedule = preferredId
        ? this.schedules.find((schedule) => schedule.dailyScheduleId === preferredId) || null
        : null;

      // The id in the route is authoritative. It may belong to a company other
      // than the one selected by default when this component starts.
      if (requestedSchedule) {
        await this.selectSchedule(requestedSchedule.dailyScheduleId, false, navigateSelection);
      } else if (preferredId) {
        await this.selectSchedule(preferredId, false, navigateSelection);
        await this.reloadSchedulesForSelectedCompany(preferredId);
      } else if (this.schedules[0]) {
        await this.selectSchedule(this.schedules[0].dailyScheduleId, false, navigateSelection);
      } else {
        this.selectedScheduleId = null;
        this.selectedSchedule = null;
        this.mode = 'view';
      }
    } catch (error) {
      console.error('Error loading daily schedules', error);
      this.schedules = [];
      this.dataSource.data = [];
      this.selectedScheduleId = null;
      this.selectedSchedule = null;
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('dailySchedulesCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async reloadSchedulesForSelectedCompany(selectedScheduleId: string): Promise<void> {
    if (!this.selectedCompanyId) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.schedules = (response || []).map((schedule) => this.normalizeSchedule(schedule));
      this.dataSource.data = this.schedules;

      const selectedFromCatalog = this.schedules.find(
        (schedule) => schedule.dailyScheduleId === selectedScheduleId
      );
      if (selectedFromCatalog) {
        this.selectedSchedule = selectedFromCatalog;
        this.form = this.createFormFromSchedule(selectedFromCatalog);
      }
    } catch (error) {
      // The detail was already loaded by id, so keep it available for editing.
      console.error('Error syncing daily schedules for selected company', error);
    }
  }

  async selectSchedule(
    dailyScheduleId: string,
    showLoader = true,
    navigate = true
  ): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }

    if (showLoader) {
      this.isLoading = true;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DailyScheduleConfig>(
          `labor-config/daily-schedules/${dailyScheduleId}`
        )
      );
      const normalizedSchedule = this.normalizeSchedule(response);

      this.selectedScheduleId = normalizedSchedule.dailyScheduleId;
      this.selectedSchedule = normalizedSchedule;
      this.selectedCompanyId = normalizedSchedule.companiesRecordId || this.selectedCompanyId;
      await this.loadJourneys();
      this.mode = 'view';
      this.form = this.createFormFromSchedule(normalizedSchedule);
      if (navigate) {
        void this.router.navigate(['/attendance/daily-schedules', normalizedSchedule.dailyScheduleId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
    } catch (error) {
      console.error('Error loading daily schedule detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(
          error,
          this.t('dailySchedulesCatalog.notifications.loadDetailError')
        ),
        'bottom',
        'center'
      );
    } finally {
      if (showLoader) {
        this.isLoading = false;
      }
    }
  }

  async refresh(): Promise<void> {
    await this.syncWithRoute();
  }

  onSearchChange(search: string): void {
    this.dataSource.filter = search.trim().toLowerCase();
  }

  async onCompanyChange(): Promise<void> {
    this.selectedCompanyId = this.form.companiesRecordId;
    await this.loadJourneys();
  }

  onJourneyChange(journeyId: string): void {
    const journey = this.journeys.find((item) => item.journeyId === journeyId);
    const mealBreakMinutes = Number(journey?.mealBreakMinutes || 0);

    if (journey?.includesMealBreak && mealBreakMinutes > 0) {
      this.form.mealBreak.enabled = true;
      this.form.mealBreak.minutes = mealBreakMinutes;
      return;
    }

    this.form.mealBreak.enabled = false;
    this.onMealBreakToggle();
  }

  openTimePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  onRowClick(schedule: DailyScheduleConfig): void {
    void this.router.navigate(['/attendance/daily-schedules', schedule.dailyScheduleId]);
  }

  onEditRow(schedule: DailyScheduleConfig): void {
    if (!this.hasAccess('update')) {
      return;
    }

    void this.router.navigate(['/attendance/daily-schedules', schedule.dailyScheduleId, 'edit'], {
      queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
    });
  }

  onDeleteRow(schedule: DailyScheduleConfig): void {
    void this.selectSchedule(schedule.dailyScheduleId, false, false).then(() => this.deleteSelected());
  }

  startCreate(): void {
    this.enterCreateMode(true);
  }

  private enterCreateMode(navigate = false): void {
    if (!this.hasAccess('create')) {
      return;
    }

    this.mode = 'create';
    this.selectedScheduleId = null;
    this.selectedSchedule = null;
    this.selectedCompanyId = this.getDefaultCompanyId();
    this.form = this.createEmptyForm();

    if (navigate) {
      void this.router.navigate(['/attendance/daily-schedules/new'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  startEdit(): void {
    this.enterEditMode(true);
  }

  private enterEditMode(navigate = false): void {
    if (!this.selectedSchedule || !this.hasAccess('update')) {
      return;
    }

    this.mode = 'edit';
    this.form = this.createFormFromSchedule(this.selectedSchedule);

    if (navigate && this.selectedScheduleId) {
      void this.router.navigate(['/attendance/daily-schedules', this.selectedScheduleId, 'edit'], {
        queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
      });
    }
  }

  cancelEdit(): void {
    this.exitEditMode(true);
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/daily-schedules']);
  }

  private exitEditMode(navigate = false): void {
    if (this.selectedSchedule) {
      this.mode = 'view';
      this.form = this.createFormFromSchedule(this.selectedSchedule);
      if (navigate && this.selectedScheduleId) {
        void this.router.navigate(['/attendance/daily-schedules', this.selectedScheduleId], {
          queryParams: this.selectedCompanyId ? { companyId: this.selectedCompanyId } : undefined,
        });
      }
      return;
    }

    if (this.schedules.length) {
      const firstScheduleId = this.schedules[0].dailyScheduleId;
      if (navigate) {
        void this.router.navigate(['/attendance/daily-schedules', firstScheduleId]);
      } else {
        void this.selectSchedule(firstScheduleId, false, false);
      }
      return;
    }

    this.mode = 'view';
    this.form = this.createEmptyForm();
    if (navigate) {
      void this.router.navigate(['/attendance/daily-schedules']);
    }
  }

  private async syncWithRoute(): Promise<void> {
    const dailyScheduleId = this.route.snapshot.paramMap.get('dailyScheduleId');
    const companyId = this.route.snapshot.queryParamMap.get('companyId') || '';
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/daily-schedules/new');
    const isEditRoute = /\/attendance\/daily-schedules\/[^/]+\/edit$/.test(currentUrl);

    if (
      companyId &&
      this.companies.some((company) => company.companiesRecordId === companyId) &&
      companyId !== this.selectedCompanyId
    ) {
      this.selectedCompanyId = companyId;
    }

    await this.loadSchedules(dailyScheduleId, false);

    if (isCreateRoute) {
      this.enterCreateMode(false);
      return;
    }

    if (dailyScheduleId && isEditRoute) {
      this.enterEditMode(false);
    }
  }

  onModeChange(): void {
    if (!this.supportsCrossesMidnight) {
      this.form.crossesMidnight = false;
    }

    if (!this.usesEntryWindow) {
      this.form.entryWindowStartTime = '';
      this.form.entryWindowEndTime = '';
    }

    if (!this.usesWorkMinuteLimits) {
      this.form.minimumWorkMinutes = '';
      this.form.maximumWorkMinutes = '';
    }

    if (!this.isSplitShiftMode) {
      this.form.blocks = [];
    } else if (!this.form.blocks.length) {
      this.addBlock();
    }

    if (!this.usesFixedTimeRange) {
      this.form.startTime = '';
      this.form.endTime = '';
    } else if (this.form.mode === 'fixed_start_flexible_end') {
      this.form.endTime = '';
    }
  }

  onMealBreakToggle(): void {
    if (!this.form.mealBreak.enabled) {
      this.form.mealBreak = {
        enabled: false,
        required: false,
        mode: 'fixed',
        minutes: '',
        startTime: '',
        endTime: '',
        flexibleWindowStartTime: '',
        flexibleWindowEndTime: '',
      };
    }
  }

  onMealBreakModeChange(): void {
    this.form.mealBreak.startTime = '';
    this.form.mealBreak.endTime = '';
    this.form.mealBreak.flexibleWindowStartTime = '';
    this.form.mealBreak.flexibleWindowEndTime = '';
  }

  addBlock(): void {
    this.form.blocks = [
      ...this.form.blocks,
      {
        blockOrder: this.form.blocks.length + 1,
        startTime: '',
        endTime: '',
      },
    ];
  }

  removeBlock(index: number): void {
    this.form.blocks = this.form.blocks
      .filter((_, currentIndex) => currentIndex !== index)
      .map((block, currentIndex) => ({
        ...block,
        blockOrder: currentIndex + 1,
      }));
  }

  trackBlock(index: number): number {
    return index;
  }
  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const payload = this.buildCreatePayload();
        const createdSchedule = await firstValueFrom(
          this.apiService.post<DailyScheduleConfig>('labor-config/daily-schedules', payload)
        );
        const createdScheduleId = createdSchedule?.dailyScheduleId || null;
        await this.loadSchedules(createdScheduleId, false);
        this.showNotification(
          'snackbar-success',
          this.t('dailySchedulesCatalog.notifications.created'),
          'bottom',
          'center'
        );
        if (createdScheduleId) {
          void this.router.navigate(['/attendance/daily-schedules', createdScheduleId]);
        }
      } else if (this.isEditMode && this.selectedScheduleId) {
        const payload = this.buildUpdatePayload();
        await firstValueFrom(
          this.apiService.patch(
            `labor-config/daily-schedules/${this.selectedScheduleId}`,
            payload
          )
        );
        await this.loadSchedules(this.selectedScheduleId, false);
        this.showNotification(
          'snackbar-success',
          this.t('dailySchedulesCatalog.notifications.updated'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/daily-schedules', this.selectedScheduleId]);
      }
    } catch (error) {
      console.error('Error saving daily schedule', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('dailySchedulesCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedSchedule || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('dailySchedulesCatalog.dialog.deleteTitle'),
        message: this.t('dailySchedulesCatalog.dialog.deleteMessage', {
          schedule: this.selectedSchedule.name,
        }),
        confirmLabel: this.t('dailySchedulesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedScheduleId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`labor-config/daily-schedules/${this.selectedScheduleId}`)
        );
        await this.loadSchedules(null, false);
        this.showNotification(
          'snackbar-success',
          this.t('dailySchedulesCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/daily-schedules']);
      } catch (error) {
        console.error('Error deleting daily schedule', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(
            error,
            this.t('dailySchedulesCatalog.notifications.deleteError')
          ),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  getModeLabel(mode: DailyScheduleMode): string {
    return this.t(`dailySchedulesCatalog.modeOptions.${mode}`);
  }

  getMealBreakModeLabel(mode: MealBreakMode | undefined): string {
    return this.t(`dailySchedulesCatalog.mealBreakModes.${mode || 'fixed'}`);
  }

  getScheduleSummary(schedule: DailyScheduleConfig | DailyScheduleFormValue): string {
    const range = this.getRangeLabel(schedule);
    const overnight = schedule.crossesMidnight
      ? this.t('dailySchedulesCatalog.labels.overnight')
      : '';

    return [range, overnight].filter(Boolean).join(' | ') || this.t('dailySchedulesCatalog.labels.noRange');
  }

  getRangeLabel(schedule: Partial<DailyScheduleConfig> | DailyScheduleFormValue): string {
    if (schedule.mode === 'entry_window_duration') {
      if (schedule.entryWindowStartTime && schedule.entryWindowEndTime) {
        return this.t('dailySchedulesCatalog.labels.entryWindowSummary', {
          start: schedule.entryWindowStartTime,
          end: schedule.entryWindowEndTime,
        });
      }
      return this.t('dailySchedulesCatalog.labels.entryWindow');
    }

    if (schedule.mode === 'open_shift_with_limits') {
      return this.t('dailySchedulesCatalog.labels.workLimitsSummary', {
        min: Number(schedule.minimumWorkMinutes || 0),
        max: Number(schedule.maximumWorkMinutes || 0),
      });
    }

    if (schedule.mode === 'split_shift') {
      return this.t('dailySchedulesCatalog.labels.splitShiftSummary', {
        count: schedule.blocks?.length || 0,
      });
    }

    if (schedule.mode === 'hours_only') {
      return this.t('dailySchedulesCatalog.labels.hoursOnly');
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    if (schedule.startTime) {
      return this.t('dailySchedulesCatalog.labels.startOnly', { start: schedule.startTime });
    }

    return this.t('dailySchedulesCatalog.labels.noRange');
  }

  getMealBreakSummary(schedule: DailyScheduleConfig | DailyScheduleFormValue): string {
    if (!schedule.mealBreak.enabled) {
      return this.t('dailySchedulesCatalog.labels.noMealBreak');
    }

    return this.t('dailySchedulesCatalog.labels.mealBreakSummary', {
      mode: this.getMealBreakModeLabel(schedule.mealBreak.mode).toLowerCase(),
      minutes: Number(schedule.mealBreak.minutes || 0),
    });
  }

  getToleranceSummary(schedule: DailyScheduleConfig): string {
    const toleranceRules = schedule.toleranceRules || {};
    return this.t('dailySchedulesCatalog.labels.toleranceSummary', {
      entry: Number(toleranceRules.entryToleranceMinutes || 0),
      exit: Number(toleranceRules.earlyExitToleranceMinutes || 0),
      rounding: Number(toleranceRules.roundingMarginMinutes || 0),
    });
  }

  getOvertimeSummary(schedule: DailyScheduleConfig): string {
    const overtimeRules = schedule.overtimeRules || {};
    return this.t('dailySchedulesCatalog.labels.overtimeSummary', {
      minutes: Number(overtimeRules.minimumMinutesBeforeOvertime || 0),
      auth: overtimeRules.requiresAuthorization
        ? this.t('dailySchedulesCatalog.labels.authorizationRequired')
        : this.t('dailySchedulesCatalog.labels.authorizationOptional'),
    });
  }

  getJourneyName(journeyId: string | null | undefined): string {
    if (!journeyId) {
      return '-';
    }

    return this.journeys.find((journey) => journey.journeyId === journeyId)?.name || '-';
  }

  private formatClockTime(value?: string | null): string {
    if (!value || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) return value || '—';
    const [hourText, minute] = value.split(':');
    const hour = Number(hourText);
    const suffix = hour >= 12 ? 'p.m.' : 'a.m.';
    const displayHour = hour % 12 || 12;
    return `${displayHour.toString().padStart(2, '0')}:${minute} ${suffix}`;
  }

  private durationBetweenTimes(start: string, end: string): number {
    const startMinutes = this.timeToMinutes(start);
    const endMinutes = this.timeToMinutes(end);
    if (startMinutes === null || endMinutes === null) return 0;
    return endMinutes >= startMinutes ? endMinutes - startMinutes : (24 * 60 - startMinutes) + endMinutes;
  }

  private timeToMinutes(value?: string | null): number | null {
    if (!value || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) return null;
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }
  private createEmptyForm(): DailyScheduleFormValue {
    return {
      companiesRecordId: this.getDefaultCompanyId(),
      journeyId: '',
      name: '',
      mode: 'fixed_full',
      startTime: '',
      endTime: '',
      crossesMidnight: false,
      entryWindowStartTime: '',
      entryWindowEndTime: '',
      minimumWorkMinutes: '',
      maximumWorkMinutes: '',
      mealBreak: {
        enabled: false,
        required: false,
        mode: 'fixed',
        minutes: '',
        startTime: '',
        endTime: '',
        flexibleWindowStartTime: '',
        flexibleWindowEndTime: '',
      },
      blocks: [],
    };
  }

  private createFormFromSchedule(schedule: DailyScheduleConfig): DailyScheduleFormValue {
    return {
      companiesRecordId: schedule.companiesRecordId || this.selectedCompanyId,
      journeyId: schedule.journeyId || '',
      name: schedule.name,
      mode: schedule.mode,
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      crossesMidnight: schedule.crossesMidnight,
      entryWindowStartTime: schedule.entryWindowStartTime || '',
      entryWindowEndTime: schedule.entryWindowEndTime || '',
      minimumWorkMinutes: schedule.minimumWorkMinutes ?? '',
      maximumWorkMinutes: schedule.maximumWorkMinutes ?? '',
      mealBreak: {
        enabled: schedule.mealBreak.enabled,
        required: !!schedule.mealBreak.required,
        mode: schedule.mealBreak.mode || 'fixed',
        minutes: schedule.mealBreak.minutes ?? '',
        startTime: schedule.mealBreak.startTime || '',
        endTime: schedule.mealBreak.endTime || '',
        flexibleWindowStartTime: schedule.mealBreak.flexibleWindowStartTime || '',
        flexibleWindowEndTime: schedule.mealBreak.flexibleWindowEndTime || '',
      },
      blocks: (schedule.blocks || []).map((block) => ({
        blockOrder: block.blockOrder,
        startTime: block.startTime,
        endTime: block.endTime,
      })),
    };
  }

  private validateForm(): string | null {
    if (!this.form.companiesRecordId) {
      return this.t('orgUnitsCatalog.validationCompany');
    }

    if (!this.form.journeyId) {
      return this.t('dailySchedulesCatalog.notifications.validationJourney');
    }

    const name = this.normalizeOptionalText(this.form.name);

    if (!name) {
      return this.t('dailySchedulesCatalog.notifications.validationName');
    }

    if (this.usesFixedTimeRange && !this.isValidTime(this.form.startTime)) {
      return this.t('dailySchedulesCatalog.notifications.validationStartTime');
    }

    if (this.form.mode === 'fixed_full' && !this.isValidTime(this.form.endTime)) {
      return this.t('dailySchedulesCatalog.notifications.validationEndTime');
    }

    if (this.usesEntryWindow) {
      if (!this.isValidTime(this.form.entryWindowStartTime)) {
        return this.t('dailySchedulesCatalog.notifications.validationEntryWindowStart');
      }

      if (!this.isValidTime(this.form.entryWindowEndTime)) {
        return this.t('dailySchedulesCatalog.notifications.validationEntryWindowEnd');
      }
    }

    if (this.usesWorkMinuteLimits) {
      if (!this.isPositiveNumber(this.form.minimumWorkMinutes)) {
        return this.t('dailySchedulesCatalog.notifications.validationMinimumWorkMinutes');
      }

      if (!this.isPositiveNumber(this.form.maximumWorkMinutes)) {
        return this.t('dailySchedulesCatalog.notifications.validationMaximumWorkMinutes');
      }
    }

    if (this.isSplitShiftMode) {
      if (!this.form.blocks.length) {
        return this.t('dailySchedulesCatalog.notifications.validationBlocks');
      }

      const hasInvalidBlock = this.form.blocks.some(
        (block) => !this.isValidTime(block.startTime) || !this.isValidTime(block.endTime)
      );
      if (hasInvalidBlock) {
        return this.t('dailySchedulesCatalog.notifications.validationBlockTimes');
      }
    }

    if (this.mealBreakEnabled) {
      if (!this.isPositiveNumber(this.form.mealBreak.minutes)) {
        return this.t('dailySchedulesCatalog.notifications.validationMealBreakMinutes');
      }

      if (this.fixedMealBreakMode) {
        if (!this.isValidTime(this.form.mealBreak.startTime)) {
          return this.t('dailySchedulesCatalog.notifications.validationMealBreakStart');
        }

        if (!this.isValidTime(this.form.mealBreak.endTime)) {
          return this.t('dailySchedulesCatalog.notifications.validationMealBreakEnd');
        }
      }

      if (this.flexibleMealBreakMode) {
        if (!this.isValidTime(this.form.mealBreak.flexibleWindowStartTime)) {
          return this.t('dailySchedulesCatalog.notifications.validationMealBreakWindowStart');
        }

        if (!this.isValidTime(this.form.mealBreak.flexibleWindowEndTime)) {
          return this.t('dailySchedulesCatalog.notifications.validationMealBreakWindowEnd');
        }
      }
    }

    return null;
  }

  private buildCreatePayload(): DailyScheduleCreatePayload {
    return this.buildPayload();
  }

  private buildUpdatePayload(): DailyScheduleUpdatePayload {
    return this.buildPayload();
  }

  private buildPayload(): DailyScheduleCreatePayload {
    const payload: DailyScheduleCreatePayload = {
      companiesRecordId: this.form.companiesRecordId,
      journeyId: this.form.journeyId,
      name: this.normalizeOptionalText(this.form.name) || '',
      mode: this.form.mode,
      crossesMidnight: this.supportsCrossesMidnight ? this.form.crossesMidnight : false,
      mealBreak: this.buildMealBreakPayload(),
      blocks: this.isSplitShiftMode
        ? this.form.blocks.map((block, index) => ({
          blockOrder: index + 1,
          startTime: block.startTime,
          endTime: block.endTime,
        }))
        : [],
    };

    if (this.form.startTime) {
      payload.startTime = this.form.startTime;
    }
    if (this.form.endTime) {
      payload.endTime = this.form.endTime;
    }
    if (this.form.entryWindowStartTime) {
      payload.entryWindowStartTime = this.form.entryWindowStartTime;
    }
    if (this.form.entryWindowEndTime) {
      payload.entryWindowEndTime = this.form.entryWindowEndTime;
    }
    if (this.form.minimumWorkMinutes !== '' && Number(this.form.minimumWorkMinutes) > 0) {
      payload.minimumWorkMinutes = Number(this.form.minimumWorkMinutes);
    }
    if (this.form.maximumWorkMinutes !== '' && Number(this.form.maximumWorkMinutes) > 0) {
      payload.maximumWorkMinutes = Number(this.form.maximumWorkMinutes);
    }

    return payload;
  }

  private buildMealBreakPayload(): DailyScheduleMealBreak {
    if (!this.form.mealBreak.enabled) {
      return {
        enabled: false,
        required: false,
      };
    }

    const payload: DailyScheduleMealBreak = {
      enabled: true,
      required: this.form.mealBreak.required,
      mode: this.form.mealBreak.mode,
      minutes: Number(this.form.mealBreak.minutes),
    };

    if (this.fixedMealBreakMode) {
      payload.startTime = this.form.mealBreak.startTime;
      payload.endTime = this.form.mealBreak.endTime;
    }

    if (this.flexibleMealBreakMode) {
      payload.flexibleWindowStartTime = this.form.mealBreak.flexibleWindowStartTime;
      payload.flexibleWindowEndTime = this.form.mealBreak.flexibleWindowEndTime;
    }

    return payload;
  }

  private async loadCompanies(preserveSelection = true): Promise<void> {
    this.isLoading = true;
    try {
      const response = await this.companyDirectory.getAllActive();

      this.companies = this.normalizeCompanies(response).filter((company) => company.isActive !== false).sort((left, right) => left.name.localeCompare(right.name));
      if (!preserveSelection || !this.selectedCompanyId || !this.companies.some((company) => company.companiesRecordId === this.selectedCompanyId)) {
        this.selectedCompanyId = this.companies[0]?.companiesRecordId || '';
      }
    } catch (error) {
      console.error('Error loading companies for daily schedules', error);
      this.companies = [];
      this.selectedCompanyId = '';
    }
  }

  private async loadJourneys(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.journeys = [];
      this.form.journeyId = '';
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<JourneyConfig[]>('labor-config/journeys', {
          params: { companiesRecordId: this.selectedCompanyId },
        })
      );

      this.journeys = (response || []).filter((journey) => journey.isActive !== false);
      if (this.form.journeyId && !this.journeys.some((journey) => journey.journeyId === this.form.journeyId)) {
        this.form.journeyId = '';
      }
    } catch (error) {
      console.error('Error loading journeys for daily schedules', error);
      this.journeys = [];
      this.form.journeyId = '';
    }
  }

  private getDefaultCompanyId(): string {
    if (this.companies.length === 1) {
      return this.selectedCompanyId || this.companies[0]?.companiesRecordId || '';
    }

    return '';
  }

  private normalizeCompanies(
    response: CompaniesRecord[] | CompaniesRecordsResponse | null | undefined
  ): CompaniesRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response?.items || response?.data || response?.results || response?.companiesRecords || [];
  }

  private normalizeSchedule(schedule: Partial<DailyScheduleConfig>): DailyScheduleConfig {
    const mode = (schedule.mode || 'fixed_full') as DailyScheduleMode;
    const mealBreak = schedule.mealBreak || { enabled: false };
    const toleranceRules = schedule.toleranceRules || {};
    const overtimeRules = schedule.overtimeRules || {};
    const blocks = (schedule.blocks || []).map((block, index) => ({
      blockOrder: block.blockOrder || index + 1,
      startTime: block.startTime || '',
      endTime: block.endTime || '',
    }));
    const isActive = schedule.isActive !== false;

    const normalized: DailyScheduleConfig = {
      dailyScheduleId: schedule.dailyScheduleId || crypto.randomUUID(),
      center: schedule.center || '',
      companiesRecordId: schedule.companiesRecordId,
      journeyId: schedule.journeyId || '',
      name: schedule.name || '',
      mode,
      startTime: schedule.startTime || '',
      endTime: schedule.endTime || '',
      crossesMidnight: !!schedule.crossesMidnight,
      entryWindowStartTime: schedule.entryWindowStartTime || '',
      entryWindowEndTime: schedule.entryWindowEndTime || '',
      minimumWorkMinutes:
        schedule.minimumWorkMinutes !== undefined ? Number(schedule.minimumWorkMinutes) : undefined,
      maximumWorkMinutes:
        schedule.maximumWorkMinutes !== undefined ? Number(schedule.maximumWorkMinutes) : undefined,
      mealBreak: {
        enabled: !!mealBreak.enabled,
        required: !!mealBreak.required,
        mode: (mealBreak.mode || 'fixed') as MealBreakMode,
        minutes: mealBreak.minutes !== undefined ? Number(mealBreak.minutes) : undefined,
        startTime: mealBreak.startTime || '',
        endTime: mealBreak.endTime || '',
        flexibleWindowStartTime: mealBreak.flexibleWindowStartTime || '',
        flexibleWindowEndTime: mealBreak.flexibleWindowEndTime || '',
      },
      toleranceRules: {
        entryToleranceMinutes:
          toleranceRules.entryToleranceMinutes !== undefined
            ? Number(toleranceRules.entryToleranceMinutes)
            : undefined,
        earlyExitToleranceMinutes:
          toleranceRules.earlyExitToleranceMinutes !== undefined
            ? Number(toleranceRules.earlyExitToleranceMinutes)
            : undefined,
        roundingMarginMinutes:
          toleranceRules.roundingMarginMinutes !== undefined
            ? Number(toleranceRules.roundingMarginMinutes)
            : undefined,
      },
      overtimeRules: {
        minimumMinutesBeforeOvertime:
          overtimeRules.minimumMinutesBeforeOvertime !== undefined
            ? Number(overtimeRules.minimumMinutesBeforeOvertime)
            : undefined,
        requiresAuthorization: !!overtimeRules.requiresAuthorization,
      },
      blocks,
      isActive,
    };

    normalized.modeLabel = this.getModeLabel(normalized.mode);
    normalized.scheduleSummary = this.getScheduleSummary(normalized);
    normalized.mealBreakSummary = this.getMealBreakSummary(normalized);
    normalized.activeLabel = isActive
      ? this.t('common.status.active')
      : this.t('common.status.inactive');

    return normalized;
  }

  private isValidTime(value: string): boolean {
    if (!value) {
      return false;
    }
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
  }

  private isPositiveNumber(value: number | ''): boolean {
    return value !== '' && Number.isFinite(Number(value)) && Number(value) > 0;
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }
    const normalizedValue = value.trim();
    return normalizedValue || undefined;
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private configureTable(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('dailySchedulesCatalog.actions.create'),
        colorClass: 'success',
        auth: this.createAccess,
        behavior: 'add',
      },
      {
        id: 'refresh',
        icon: 'refresh',
        tooltip: this.t('common.reload'),
        colorClass: 'neutral',
        auth: this.readAccess,
        behavior: 'refresh',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('common.update'),
        colorClass: 'accent',
        auth: this.updateAccess,
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'delete',
        icon: 'delete_outline',
        tooltip: this.t('common.delete'),
        colorClass: 'danger',
        auth: this.deleteAccess,
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'name', label: this.t('dailySchedulesCatalog.fields.name'), type: 'text', visible: true },
      {
        def: 'scheduleSummary',
        label: this.t('dailySchedulesCatalog.fields.scheduleSummary'),
        type: 'text',
        visible: true,
      },
      {
        def: 'mealBreakSummary',
        label: this.t('dailySchedulesCatalog.fields.mealBreak'),
        type: 'text',
        visible: true,
      },
      {
        def: 'modeLabel',
        label: this.t('dailySchedulesCatalog.fields.mode'),
        type: 'text',
        visible: true,
      },
      {
        def: 'activeLabel',
        label: this.t('dailySchedulesCatalog.fields.status'),
        type: 'status',
        visible: true,
        statusBadgeMap: {
          [this.t('common.status.active')]: 'badge badge-solid-green',
          [this.t('common.status.inactive')]: 'badge badge-solid-red',
        },
      },
    ];
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'dailySchedules', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
